// Snapshot utilities — client-side snapshot computation for Phase 1.
// In Phase 2+ this moves to the backend snapshot_job.py per SPEC §15.
//
// Storage layout:
//   localStorage["verdant_snapshots_<pollId>"] = JSON-serialized array of
//   snapshot objects, capped at the most recent 52 entries (~1y weekly).

const KEY_PREFIX = 'verdant_snapshots_'
const MAX_SNAPSHOTS = 52

// Take a point-in-time tally from current vote data.
// `votes` is the per-poll tally shape: { Yes: { resident, nonResident }, ... }
// We read both Unsure (current) and Abstain (legacy on Nostr relays) for
// the third option so historic vote events keep counting correctly.
export function computeSnapshot(votes, billStage = 'introduced') {
  const yesR    = votes?.Yes?.resident      || 0
  const noR     = votes?.No?.resident       || 0
  const unsureR = (votes?.Unsure?.resident    ?? votes?.Abstain?.resident)    || 0
  const yesNR   = votes?.Yes?.nonResident   || 0
  const noNR    = votes?.No?.nonResident    || 0
  const unsureNR = (votes?.Unsure?.nonResident ?? votes?.Abstain?.nonResident) || 0

  const total = yesR + noR + unsureR + yesNR + noNR + unsureNR
  const residentTotal = yesR + noR + unsureR

  return {
    snapshot_at: new Date().toISOString(),
    bill_stage: billStage,
    yes_resident: yesR,
    no_resident: noR,
    unsure_resident: unsureR,
    yes_nonresident: yesNR,
    no_nonresident: noNR,
    unsure_nonresident: unsureNR,
    total_voters: total,
    yes_pct: residentTotal > 0 ? Math.round((yesR / residentTotal) * 100) : 0,
    no_pct:  residentTotal > 0 ? Math.round((noR  / residentTotal) * 100) : 0,
  }
}

// Load stored snapshots (oldest first) for a poll.
export function loadSnapshots(pollId) {
  try {
    const raw = localStorage.getItem(KEY_PREFIX + pollId)
    return raw ? JSON.parse(raw) : []
  } catch {
    return []
  }
}

// Append a snapshot, retaining the most recent MAX_SNAPSHOTS.
export function saveSnapshot(pollId, snapshot) {
  const existing = loadSnapshots(pollId)
  const updated = [...existing, snapshot].slice(-MAX_SNAPSHOTS)
  try {
    localStorage.setItem(KEY_PREFIX + pollId, JSON.stringify(updated))
  } catch { /* quota — silently drop */ }
  return updated
}

// Direction of sentiment change over the last few snapshots.
// Returns 'rising' | 'falling' | 'stable' | 'insufficient'.
export function sentimentDirection(snapshots) {
  if (!snapshots || snapshots.length < 2) return 'insufficient'
  const recent = snapshots.slice(-3)
  const first = recent[0].yes_pct
  const last = recent[recent.length - 1].yes_pct
  const delta = last - first
  if (delta > 3) return 'rising'
  if (delta < -3) return 'falling'
  return 'stable'
}

export function directionArrow(direction) {
  return ({
    rising: '↑',
    falling: '↓',
    stable: '→',
    insufficient: '',
  })[direction] || ''
}

// Phase 1: take at most one snapshot per day per poll.
export function shouldSnapshot(pollId) {
  const snapshots = loadSnapshots(pollId)
  if (snapshots.length === 0) return true
  const last = new Date(snapshots[snapshots.length - 1].snapshot_at)
  const daysSince = (Date.now() - last.getTime()) / (1000 * 60 * 60 * 24)
  return daysSince >= 1
}

// Enumerate all poll IDs we currently have snapshots for. Used by
// Analytics to find "biggest movers" without needing to know which polls
// the user has interacted with.
export function listSnapshottedPolls() {
  const ids = []
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k && k.startsWith(KEY_PREFIX)) {
        ids.push(k.slice(KEY_PREFIX.length))
      }
    }
  } catch { /* noop */ }
  return ids
}

// Compute Yes% delta between the most recent snapshot and the one before
// it. Returns null if fewer than 2 snapshots exist.
export function recentYesDelta(snapshots) {
  if (!snapshots || snapshots.length < 2) return null
  const last = snapshots[snapshots.length - 1]
  const prev = snapshots[snapshots.length - 2]
  return last.yes_pct - prev.yes_pct
}
